import axios, { type AxiosInstance, type AxiosError } from 'axios'
import { log } from '@/utils/logger'
import { getMessage } from '@/utils/messages'
import { useUiStore } from '@/store/uiStore'

// ── Base configuration ────────────────────────────────────────
const resolve = (runtimeVal: string | undefined, buildVal: string) =>
  runtimeVal && !runtimeVal.startsWith('${') ? runtimeVal : buildVal

const backendUrl = resolve(window.APP_CONFIG?.BACKEND_URL, import.meta.env.VITE_BACKEND_SERVICE_URL)
const apiTimeout = resolve(window.APP_CONFIG?.API_TIMEOUT, import.meta.env.VITE_API_TIMEOUT)

// In dev, use a relative base URL so requests go through Vite's proxy (avoids CORS).
// In production, use the absolute backend URL from config.
const baseURL = import.meta.env.DEV ? '/api' : backendUrl

const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: Number(apiTimeout ?? 30000),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

log.info('API client initialised', { baseURL: apiClient.defaults.baseURL })

// ── Request interceptor: inject auth token ────────────────────
apiClient.interceptors.request.use(
  (config) => {
    if (!navigator.onLine) {
      log.warn('API request blocked — device is offline', { url: config.url })
      return Promise.reject(new Error(getMessage('api.error.offline')))
    }

    const rateLimitedUntil = useUiStore.getState().rateLimitedUntil
    if (rateLimitedUntil && Date.now() < rateLimitedUntil) {
      const seconds = Math.ceil((rateLimitedUntil - Date.now()) / 1000)
      log.warn('API request blocked — rate limited', { url: config.url, seconds: String(seconds) })
      return Promise.reject(
        new Error(getMessage('api.error.rate_limited', { seconds: String(seconds), plural: seconds !== 1 ? 's' : '' }))
      )
    }

    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    const traceId = `viq-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`
    config.headers['X-Trace-ID'] = traceId

    log.info('API request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      traceId,
      hasAuth: String(!!token),
    })

    return config
  },
  (error) => Promise.reject(error)
)

// ── Response interceptor: normalize errors + 401 token refresh ───

// Global auth state machine — makes session-expiry handling deterministic when
// several requests fail with 401 at once (e.g. after a long idle period wakes up
// multiple queries together). Without this, each failing request independently
// refreshes/logs out/redirects, which can hammer the backend and produce a burst
// of 429s instead of a single clean redirect to login.
enum AuthState {
  Authenticated = 'authenticated',
  Refreshing = 'refreshing',
  LoggingOut = 'loggingOut',
  LoggedOut = 'loggedOut',
}

let authState: AuthState = AuthState.Authenticated
let refreshPromise: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise

  authState = AuthState.Refreshing

  // Use apiClient (not bare axios) so the request goes through the Vite proxy
  // in dev — this ensures the refresh cookie (set on localhost:5173) is included.
  // The /auth/refresh URL is excluded from the response interceptor to prevent loops.
  refreshPromise = apiClient
    .post('/auth/refresh', {}, { withCredentials: true })
    .then((res) => {
      const newToken: string = res.data.data.accessToken
      localStorage.setItem('auth_token', newToken)
      log.info('API token refresh succeeded')
      authState = AuthState.Authenticated
      return newToken
    })
    .catch((err) => {
      // Any failure (401/403/429/5xx/network) means the session is unusable —
      // never retry the refresh call automatically.
      authState = AuthState.LoggingOut
      throw err
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

// Coalesces concurrent forceLogout() calls (e.g. several requests failing at
// once after refresh fails) into a single logout + redirect.
let logoutPromise: Promise<void> | null = null

function forceLogout(): Promise<void> {
  if (logoutPromise) return logoutPromise

  authState = AuthState.LoggingOut
  logoutPromise = (async () => {
    const { useAuthStore } = await import('@/store/authStore')
    await useAuthStore.getState().logout()
    authState = AuthState.LoggedOut
    window.location.replace(`${import.meta.env.BASE_URL}/auth-login`)
  })()

  return logoutPromise
}

apiClient.interceptors.response.use(
  (response) => {
    log.info('API response', {
      status: String(response.status),
      url: response.config.url,
      traceId: String(response.config.headers?.['X-Trace-ID'] ?? ''),
    })
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean }
    const traceId = String(originalRequest?.headers?.['X-Trace-ID'] ?? '')

    if (error.response) {
      const status = error.response.status
      const isLoginRequest = originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/refresh')
      const responseData = error.response?.data as { code?: string } | undefined
      const isInvalidToken =
        status === 401 ||
        (status === 403 && responseData?.code === 'INVALID_TOKEN')

      if ((authState === AuthState.LoggingOut || authState === AuthState.LoggedOut) && !isLoginRequest) {
        // Session is already being torn down — don't spawn another refresh/logout/redirect.
        return Promise.reject(error)
      }

      if (isInvalidToken && !originalRequest?._retry && !isLoginRequest) {
        originalRequest!._retry = true
        log.warn('API token invalid — attempting refresh', { status: String(status), traceId })

        try {
          const newToken = await refreshAccessToken()
          originalRequest!.headers!['Authorization'] = `Bearer ${newToken}`
          return apiClient(originalRequest!)
        } catch (refreshErr) {
          log.error('API token refresh failed — redirecting to login', refreshErr instanceof Error ? refreshErr : new Error(String(refreshErr)), { traceId })
          localStorage.removeItem('auth_token')
          await forceLogout()
        }
      }

      if (status === 403 && responseData?.code !== 'INVALID_TOKEN') {
        log.warn('API forbidden — insufficient permissions', { url: originalRequest?.url, traceId })
      }

      log.error('API error response', new Error(`HTTP ${status}`), {
        status: String(status),
        url: originalRequest?.url,
        code: responseData?.code,
        traceId,
      })
    } else if (error.request) {
      log.error('API network error — no response received', new Error('No response'), {
        url: originalRequest?.url,
        traceId,
      })
    }

    return Promise.reject(error)
  }
)

export default apiClient
